<template>
    <modal :parent="configurationStore.appContainerSelector">
        <template #opener="{ open }">
            <text-field name="recurrence" class="recurrence" readonly :value="state.value" @click="open" @keydown="quickSelect">
                <template #label>{{ t('widget.dollar-cost-average.recurrence-select.label') }}</template>
                <template #append>
                    <caret-icon direction="down" />
                </template>
            </text-field>
        </template>

        <template #header="{ close }">
            <view-header close-button @closed="close">{{ t('widget.dollar-cost-average.recurrence-select.modal.title') }}</view-header>
        </template>

        <template #default="{ close }">
            <list-select
                name="recurrence-select"
                :selected="[state.value]"
                @updated="
                    (data) => {
                        selectRecurrence(data.value[0]);
                        close();
                    }
                "
            >
                <template v-if="state.value" #[state.value]>
                    {{ state.value }}
                </template>
                <template v-for="option of options.filter((item) => item !== state.value)" #[option]>
                    {{ option }}
                </template>
            </list-select>
        </template>
    </modal>
</template>

<script lang="ts" setup>
import { ListSelect, ViewHeader } from '@btcdirect/components';
import { CaretIcon } from '@btcdirect/components/icons';
import { useTranslations } from '@btcdirect/composables';
import { useConfigurationStore } from '@btcdirect/stores';
import { Modal, TextField, ValidatedStringFieldData } from 'out-of-the-vue';
import { onMounted, reactive } from 'vue';

const emit = defineEmits<{ (event: 'created' | 'updated', data: ValidatedStringFieldData): void }>();

const configurationStore = useConfigurationStore();
const { t } = useTranslations();

const state = reactive<ValidatedStringFieldData>({
    name: 'recurrence',
    value: t('widget.dollar-cost-average.recurrence-select.options.monthly'),
    valid: true,
    failed: []
});

const options = [
    t('widget.dollar-cost-average.recurrence-select.options.weekly'),
    t('widget.dollar-cost-average.recurrence-select.options.monthly'),
    t('widget.dollar-cost-average.recurrence-select.options.quarterly'),
    t('widget.dollar-cost-average.recurrence-select.options.half-yearly'),
    t('widget.dollar-cost-average.recurrence-select.options.yearly'),
    t('widget.dollar-cost-average.recurrence-select.options.custom')
];

const quickSelect = (event: KeyboardEvent): void => {
    const letter = event.key.toUpperCase();
    if (!letter.match(/[A-Z]/g)) {
        return;
    }

    const found = options.filter((option) => option.startsWith(letter));
    if (!found || !found.length) {
        return;
    }

    if (found.length === 1) {
        selectRecurrence(found[0]);
    }

    const index = found.findIndex((option) => option === state.value);
    if (index === found.length - 1) {
        selectRecurrence(found[0]);
        return;
    }

    selectRecurrence(found[index + 1]);
};

const selectRecurrence = (option: string): void => {
    if (option === state.value) {
        return;
    }

    state.value = option;
    emit('updated', state);
};

onMounted(() => {
    emit('created', state);
});
</script>

<style lang="scss" scoped>
.recurrence {
    :deep(main.input:hover),
    :deep(input:hover) {
        cursor: pointer;
    }
}
</style>
